/**
 * The assistant's brain: what was asked, who answers, and what happens next.
 *
 * Everything the overlay and the spotlight need lives here, for the same reason
 * `app/AppState.tsx` exists — the Help button is on every screen, so the question
 * has to outlive any one of them.
 *
 * **One question at a time, on purpose.** There is no thread, no history, no
 * scrollback. Ask, hear the answer, be shown where to press, done. A list of past
 * messages is a thing to read and a thing to get lost in, and the person this was
 * built for pressed Help because they were already lost.
 *
 * The shape of a question:
 *
 *   1. Take a snapshot of where they are standing (`knowledge/context.ts`).
 *   2. Put it behind the app's own description (`knowledge/prompt.ts`) and send
 *      it to whichever provider is configured. Or to the built-in one, which
 *      needs nothing and still answers the common questions.
 *   3. The reply is words, tool calls, or both. Run the calls against the real
 *      app — navigate, open a panel, light up a button, fill a form in.
 *   4. Feed what happened back and let it finish its sentence. Three rounds at
 *      most: anything longer is a model going round in circles, and the person
 *      is sitting there watching a spinner.
 *
 * Two hard rules are enforced *here*, not in the prompt, because a prompt is a
 * request and this is a guarantee:
 *
 *   - Nothing in this file writes to the family's records. The tools navigate,
 *     open, point, and type into a form; the Save button is untouched. The one
 *     exception is `report`, which adds a message addressed to the
 *     administrators — and even that only after the person has pressed Send.
 *   - Nothing can reach a screen the person's mode does not have. The app map
 *     sent to the model already leaves those out, and `go` checks again.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../app/AppState';
import { isFeatureOn, isPathAllowed } from '../app/experience';
import { matchPath, useRouter } from '../app/router';
import { displayName, givenName } from '../domain/names';
import { whereIsAt } from '../domain/places';
import type { ReportKind, UUID } from '../domain/types';
import { useI18n } from '../i18n';
import { uuid } from '../lib/uuid';
import { useRepos } from '../repositories';
import { HOWTOS, LIMITS, RULES, controlById, howToById, screenById } from './knowledge/appMap';
import { screenIdFor, type AssistantContext } from './knowledge/context';
import { buildSystemPrompt } from './knowledge/prompt';
import { setPrefill, type PrefillForm } from './prefill';
import { createProvider, ProviderError, type AssistantProvider, type ToolResult, type Turn } from './providers';
import { detectServer } from './server';
import { applyServerStatus, applySharedConfig, loadSettings, onSettings, type AssistantSettings } from './settings';
import { logAssistant } from './log';
import { INTERNAL_TOOLS, TOOLS } from './tools/definitions';
import { firstNamed, firstTitled, firstUniquelyNamed, idsIn } from './tools/autoShow';
import { needsFollowUp, type ToolOutcome } from './tools/followUp';
import { bornNear, runLookup } from './tools/lookup';
import { createStt, createTts, type Listening } from './voice';
import { unlockAudio } from './voice/player';
import { voiceGender } from './voice/voiceGender';

/**
 * One question, start to finish.
 *
 *   closed → ready → listening → confirming → thinking → answer
 *               ↑                                          │
 *               └──────────────  ask again  ───────────────┘
 *
 * `confirming` is the one that is not obvious and matters most. Speech
 * recognition mishears, and the people this is for will not notice until a
 * confidently wrong answer arrives. So what it heard goes on screen, large, with
 * a few seconds to throw it away. It then sends by itself, because somebody who
 * has just asked their question out loud should not have to go hunting for a
 * Send button afterwards.
 */
export type Stage = 'closed' | 'ready' | 'listening' | 'confirming' | 'thinking' | 'answer';

/**
 * How long a silence has to last before the question is sent on its own.
 *
 * People trail off. They finish a sentence, wait to see whether anything
 * happens, and never think to press the button again — so a recording that only
 * ends when it is told to ends up being a recording that never ends. Four
 * seconds is long enough to think mid-sentence and short enough not to feel
 * abandoned.
 */
export const SILENCE_MS = 4000;

/**
 * Where the corner button was standing when it was pressed.
 *
 * The microphone in the middle of the dimmed screen and the microphone in the
 * corner are meant to be read as the same object, one having moved — so the big
 * one is handed the small one's position and flies out of it, and back into it
 * when the answer is done. Measured at the moment of the press rather than
 * looked up later: by the time the overlay is drawn the corner button is already
 * hidden, and in Hebrew it is at the other end of the screen.
 */
export interface Origin {
  /** Centre of the corner button, in viewport pixels. */
  x: number;
  y: number;
  /** Its diameter, so the big button knows how far to scale down to match. */
  size: number;
}

/** The assistant's reply, as the overlay draws it. */
export interface Answer {
  id: string;
  text: string;
  /** What the app actually did, said quietly under the answer. */
  did: string[];
  /** A walkthrough, which can be run again. */
  replay?: WalkStep[];
  /** Ready-made questions to press instead of asking again. */
  howtos?: string[];
  /** A report the person can send with one press. */
  offer?: { kind: ReportKind; title: string; detail?: string };
  error?: boolean;
}

export interface WalkStep {
  anchor?: string;
  route?: string;
  say: string;
}

interface AssistantValue {
  stage: Stage;
  /** The overlay is up (any stage but closed). */
  open: boolean;
  /**
   * Open it and start listening, in one press.
   *
   * There is no longer a "ready" moment to speak of: pressing the corner button
   * *is* asking a question, so the recording starts on the way in rather than
   * waiting behind a second press on a second microphone. The `origin` is where
   * the corner button was, so the big one can fly out of it.
   */
  openAssistant: (origin?: Origin) => void;
  close: () => void;
  /** Where the corner button was when it was pressed, for the flight out and back. */
  origin: Origin | null;

  /** What they said — live while talking, settled once they stop. */
  transcript: string;
  /**
   * Sound or words arrived in the last moment — for the microphone to show that
   * it is hearing them, not just that it is on.
   */
  hearing: boolean;
  /**
   * Start listening, or finish and send what was heard. One press each way.
   *
   * Hold-to-talk used to live here too, and it is gone on purpose. Once the
   * corner button starts the recording by itself there is no press left to hold,
   * and a button that behaves differently depending on how long your finger
   * stayed down is a button that behaves unpredictably to anybody whose hands are
   * not steady — which is most of the people this was built for.
   *
   * Throwing a question away is not this button's job: that is the ✕ in the
   * corner, which works at every stage. "I have finished" and "forget it" must
   * never be the same press.
   */
  toggleMic: () => void;

  answer: Answer | null;
  /** Typed rather than spoken — the way in for a browser with no microphone. */
  askText: (question: string) => void;

  /** Start a ready-made walkthrough by id, without asking anything. */
  runHowTo: (id: string) => void;
  /** Run a walkthrough the model built for this question. */
  showSteps: (steps: WalkStep[]) => void;

  /** The walkthrough playing right now. */
  walk: { steps: WalkStep[]; index: number } | null;
  nextStep: () => void;
  endWalk: () => void;

  sendReport: (r: { kind: ReportKind; title: string; detail?: string }) => Promise<boolean>;
  /** Starting suggestions, chosen for where they are. */
  suggestions: string[];
  settings: AssistantSettings;

  /** This browser and these settings can hear them. */
  canListen: boolean;
  /** …and can talk back at all (the "read it out" button). */
  canSpeak: boolean;
  /**
   * …and should do so without being asked — answers and walkthrough steps read
   * aloud as they appear. On by default; `Settings → The Help button` turns it
   * off for anyone who would rather it stayed quiet.
   */
  speaksAloud: boolean;
  speaking: boolean;
  speak: (text: string) => void;
  stopSpeaking: () => void;
}

const Ctx = createContext<AssistantValue | null>(null);

/** How many rounds of tool calls before we stop and show what we have. */
const MAX_ROUNDS = 3;
/**
 * The tools a real model is offered. Internal tools are the fallback's private
 * vocabulary; a model offered them would try to use them. One list, built once,
 * so every request sends it identically — the server caches it with the prompt.
 */
const MODEL_TOOLS = TOOLS.filter((x) => !INTERNAL_TOOLS.has(x.name));
/** Tools that put something on screen or take them somewhere on purpose — after one, the app does not move the screen again. */
const STEERING_TOOLS = new Set(['show', 'go', 'open', 'guide', 'fill', 'set_mode', 'set_language']);
/** The tabs that each have their own way of putting one person in front of you. */
const SHOWING_SCREENS = new Set(['tree', 'world', 'timeline', 'calendar']);
/** How much of the exchange goes back to the model. */
const KEEP_TURNS = 8;

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { t, lang, setLang } = useI18n();
  const app = useApp();
  const repos = useRepos();
  const { path, search, navigate } = useRouter();

  const [stage, setStage] = useState<Stage>('closed');
  const [transcript, setTranscript] = useState('');
  const [hearing, setHearing] = useState(false);
  const hearingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [walk, setWalk] = useState<{ steps: WalkStep[]; index: number } | null>(null);
  const [settings, setSettings] = useState<AssistantSettings>(() => loadSettings());
  const [speaking, setSpeaking] = useState(false);
  const [origin, setOrigin] = useState<Origin | null>(null);
  /**
   * Asking, by reference.
   *
   * The recogniser hands its result back to `beginListening`, which is created
   * before `ask` exists, and the callback fires long after either of them was
   * made. A ref is the honest way round that cycle — and it keeps the listening
   * setup from being rebuilt every time anything the ask loop depends on moves.
   */
  const askRef = useRef<(q: string) => void>(() => {});

  /*
   * The app state, held by reference.
   *
   * `useApp()` hands back a fresh object on every render of its provider, and
   * that provider re-renders several times a second while the tree is on screen.
   * Listing `app` in a dependency array therefore rebuilds every callback here
   * five times a second — which is wasteful, and worse than wasteful for
   * anything built on top of those callbacks: a timer built on one of them is
   * torn down and restarted before it can ever fire. Everything below reads
   * `appRef.current` instead and depends only on values that genuinely change.
   */
  const appRef = useRef(app);
  appRef.current = app;

  const turnsRef = useRef<Turn[]>([]);
  const listeningRef = useRef<Listening | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** The question that produced the current answer, for a report that needs context. */
  const lastQuestion = useRef('');
  /** The last thing that went wrong on screen — usually the reason Help was pressed. */
  const lastError = useRef<string | undefined>(undefined);
  /** Restarted on every word heard; firing it ends the recording. */
  const silence = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Which recording is current; closing moves it on, so a microphone still starting knows it was abandoned. */
  const listenRun = useRef(0);

  useEffect(() => onSettings(setSettings), []);

  /*
   * The app's own server, if it is there.
   *
   * When it is, it holds the keys and answers, listens and speaks for everybody
   * — the phone on the Wi-Fi included — so nothing on the admin screen has to be
   * filled in on any device.
   */
  useEffect(() => {
    let live = true;
    void detectServer().then((status) => {
      if (!live || !status) return;
      applyServerStatus(status);
      logAssistant(
        'setup',
        status.answer ? 'ok' : 'warn',
        status.answer
          ? `the app's server answers with ${status.answer.model}${status.speak ? `, speaks with ${status.speak.provider}` : ''}${status.listen ? `, listens with ${status.listen.provider}` : ''}`
          : "the app's server is there but has no AI key — add one to .env",
      );
    });
    return () => {
      live = false;
    };
  }, []);

  /*
   * The settings as they are *now*, for the log lines.
   *
   * A ref rather than the state, because the ask loop and the listening
   * callbacks are deliberately built once and must not be rebuilt every time a
   * voice setting moves — and a log line that names the provider from a closure
   * three renders old is a log line that lies, which is worse than none.
   */
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  /*
   * The family's half of the settings, from the family record.
   *
   * Re-read whenever the family record actually changes, so an administrator
   * typing a key reaches every device — including their own, in the tab they
   * typed it in — with nothing to reload and nothing for anybody else to type.
   *
   * It used to hang off `app.dataReady`, which reads like a change signal and is
   * not one: it is a boolean that goes true once, when the family's data has
   * finished loading, and then never moves again. So the key was written
   * correctly, reported as saved correctly, and simply never picked up by the
   * running tab — the assistant went on using whatever was configured when the
   * page loaded, which for a family setting one up for the first time is the
   * built-in fallback. Pressing Help right after saving a key gave a built-in
   * answer, and the only reasonable conclusion was that the key did not work.
   *
   * `sync.onChange` is the real signal: it fires after any committed write, in
   * this tab and in every other one.
   */
  useEffect(() => {
    let live = true;
    const read = () => {
      void repos.admin
        .getAssistantConfig()
        .then((config) => {
          if (!live) return;
          const next = applySharedConfig(config as never);
          // Only when something actually moved — this runs on every committed
          // write in the app, and a log full of "nothing changed" is a log
          // nobody reads.
          if (next.provider !== settingsRef.current.provider || next.speakWith !== settingsRef.current.speakWith || next.listenWith !== settingsRef.current.listenWith) {
            logAssistant('setup', 'ok', `the family's settings arrived: ${next.provider} answers, ${next.speakWith} speaks, ${next.listenWith} listens`);
          }
          setSettings(next);
        })
        .catch(() => {
          /* unreadable: the built-in assistant still answers */
        });
    };
    read();
    const stop = repos.sync.onChange((tables) => {
      if (tables.includes('families')) read();
    });
    return () => {
      live = false;
      stop();
    };
  }, [repos, app.dataReady, app.session]);

  const provider = useMemo<AssistantProvider>(() => createProvider(settings), [settings]);
  const stt = useMemo(() => createStt(settings), [settings]);
  const tts = useMemo(() => createTts(settings), [settings]);

  const canListen = settings.listenWith !== 'off' && settings.listen && stt.available();
  const canSpeak = settings.speakWith !== 'off' && tts.available();
  const speaksAloud = canSpeak && settings.speakAnswers;

  /*
   * Anything the browser complains about is kept, so "why isn't this working?"
   * can be answered with what actually happened rather than a guess. It never
   * leaves the device unless the person presses Send on a report.
   */
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      lastError.current = e.message?.slice(0, 300);
    };
    const onReject = (e: PromiseRejectionEvent) => {
      const r = e.reason as { message?: string } | string | undefined;
      lastError.current = (typeof r === 'string' ? r : r?.message)?.slice(0, 300);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onReject);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onReject);
    };
  }, []);

  // ---- talking ------------------------------------------------------------

  const speakNow = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      // Worth a line of its own: "it did not say anything" and "it was never
      // asked to say anything" look identical from the outside, and the second
      // is the one with a setting behind it.
      if (!canSpeak) {
        logAssistant('voice', 'warn', `nothing was asked to speak — speaking is ${settings.speakWith}, and ${settings.speakAnswers ? 'available' : 'switched off in Settings'}`);
        return;
      }
      setSpeaking(true);
      try {
        await tts.speak(text, lang, settings.voiceName || undefined);
      } catch (e) {
        logAssistant('voice', 'fail', 'nothing could be spoken', e);
      } finally {
        setSpeaking(false);
      }
    },
    [canSpeak, tts, lang, settings.voiceName, settings.speakWith, settings.speakAnswers],
  );
  // Held in a ref so the ask loop and the spotlight can speak without either of
  // them re-running every time a voice setting changes.
  const speakRef = useRef(speakNow);
  speakRef.current = speakNow;

  const speak = useCallback((text: string) => void speakRef.current(text), []);
  // Read inside the ask loop, which must not re-run when a voice setting changes.
  const speaksAloudRef = useRef(false);
  const stopSpeaking = useCallback(() => {
    tts.stop();
    setSpeaking(false);
  }, [tts]);

  speaksAloudRef.current = speaksAloud;

  // ---- the snapshot -------------------------------------------------------

  const snapshot = useCallback((): AssistantContext => {
    const app = appRef.current;
    /*
     * Named, with ids. "When was she born?" asked with Miriam's card open, or
     * "who told this one?" with a story open, is only answerable if the model
     * knows *which* — "someone's quick card" told it nothing it could act on.
     */
    const person = (id: string | null | undefined) => {
      const p = id ? app.peopleById.get(id as UUID) : undefined;
      return p ? `${displayName(p, lang)} (id ${p.id})` : undefined;
    };
    const openOnTop: string[] = [];
    if (app.panel) openOnTop.push(`${app.panel} panel`);
    if (app.adding) openOnTop.push(`add sheet (${app.adding})`);
    if (app.memorySubject) openOnTop.push('a day, opened as a constellation');
    if (app.storyId) {
      const story = app.stories.find((s) => s.id === app.storyId);
      openOnTop.push(story ? `the story "${story.title}" (id ${story.id})` : 'a story');
    }
    if (app.profileId) openOnTop.push(`the profile of ${person(app.profileId) ?? 'someone'}`);
    if (app.personCardId) openOnTop.push(`the quick card of ${person(app.personCardId) ?? 'someone'}`);

    const here = screenIdFor(path);
    const inView: string[] = [];
    const focus = person(search.get('focus'));
    if (here === 'tree' && focus) inView.push(`the tree is zoomed to ${focus}`);
    if (here === 'world' && focus) inView.push(`the globe is on ${focus}`);
    const only = person(search.get('person'));
    if (here === 'timeline' && only) inView.push(`the timeline shows only ${only}`);
    if (here === 'calendar' && only) inView.push(`the calendar is on ${only}'s next occasion`);
    if (here === 'profile' || here === 'editPerson') {
      const whose = person(matchPath('/people/:id', path)?.id ?? matchPath('/people/:id/edit', path)?.id);
      if (whose) inView.push(`${here === 'profile' ? 'the profile' : 'the edit form'} of ${whose}`);
    }

    const voice = settingsRef.current.voiceName || settingsRef.current.server?.speak?.voice || '';
    return {
      path,
      screen: here,
      open: openOnTop,
      inView,
      meGender: app.me?.gender,
      voiceGender: voiceGender(voice),
      lang,
      mode: app.experience,
      role: app.session?.role ?? 'member',
      adminMode: app.adminMode,
      canBeAdmin: app.canBeAdmin,
      me: app.me ? givenName(app.me, lang) : undefined,
      hasCircle: !!app.me,
      counts: {
        people: app.people.filter((p) => !p.archived && !p.mergedIntoId).length,
        stories: app.stories.length,
        moments: app.moments.length,
        upcoming: app.occasions.length,
        unread: app.unreadCount,
      },
      online: app.online,
      pending: app.pendingOps,
      shabbatQuiet: app.shabbatQuiet,
      lastError: lastError.current,
    };
  }, [path, search, lang]);

  // ---- the spotlight ------------------------------------------------------

  const startWalk = useCallback((steps: WalkStep[]) => {
    if (!steps.length) return;
    // The overlay dims the whole screen; a walkthrough needs the screen.
    setStage('closed');
    setWalk({ steps, index: 0 });
  }, []);

  const endWalk = useCallback(() => {
    setWalk(null);
    tts.stop();
  }, [tts]);

  const nextStep = useCallback(() => {
    setWalk((w) => {
      if (!w) return null;
      // Finishing the last step finishes the whole thing: they came to do
      // something and they have now done it. There is nothing left to read.
      if (w.index + 1 >= w.steps.length) return null;
      return { ...w, index: w.index + 1 };
    });
  }, []);

  const stepsFromHowTo = useCallback(
    (id: string): WalkStep[] | null => {
      const how = howToById(id);
      if (!how) return null;
      /*
       * Steps that only exist when something is switched off are dropped when it
       * is already on. "How do I see the timeline?" is four steps for somebody in
       * Simple mode — menu, settings, turn on Advanced, press the timeline — and
       * one step for somebody already in Advanced. Walking a person through
       * enabling a setting that is visibly already enabled is the fastest way to
       * make them stop believing anything the assistant says.
       */
      return how.steps
        .filter((s) => !s.onlyIfOff || !isFeatureOn(appRef.current.experience, s.onlyIfOff))
        .map((s) => ({ anchor: s.anchor, route: s.route, say: s.say[lang] }));
    },
    [lang],
  );

  const runHowTo = useCallback(
    (id: string) => {
      const steps = stepsFromHowTo(id);
      if (steps) startWalk(steps);
    },
    [stepsFromHowTo, startWalk],
  );

  const showSteps = useCallback((steps: WalkStep[]) => startWalk(steps), [startWalk]);

  // ---- reports ------------------------------------------------------------

  const sendReport = useCallback(
    async (r: { kind: ReportKind; title: string; detail?: string }): Promise<boolean> => {
      const app = appRef.current;
      const c = snapshot();
      const conversation: Array<{ role: 'user' | 'assistant'; text: string }> = [];
      if (lastQuestion.current) conversation.push({ role: 'user', text: lastQuestion.current });
      if (answer?.text) conversation.push({ role: 'assistant', text: answer.text });
      const res = await repos.feedback.send({
        kind: r.kind,
        title: r.title,
        detail: r.detail,
        idempotencyKey: uuid(),
        context: {
          path: c.path,
          screen: c.screen,
          open: c.open,
          lang: c.lang,
          mode: c.mode,
          role: (app.session?.role ?? 'member') as never,
          adminMode: c.adminMode,
          online: c.online,
          pending: c.pending,
          lastError: c.lastError,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : undefined,
          viewport: typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : undefined,
          assistant: `${settings.provider}${settings.model ? ` · ${settings.model}` : ''}`,
        },
        conversation,
      });
      if (res.ok) app.reload();
      return res.ok;
    },
    [repos, snapshot, answer, settings],
  );

  // ---- running one tool ---------------------------------------------------

  const runTool = useCallback(
    async (name: string, input: Record<string, unknown>): Promise<{ output: string; patch?: Partial<Answer> }> => {
      const app = appRef.current;
      const str = (k: string): string | undefined => (typeof input[k] === 'string' ? (input[k] as string).trim() || undefined : undefined);

      switch (name) {
        case 'guide': {
          const howto = str('howto');
          let steps: WalkStep[] | null = howto ? stepsFromHowTo(howto) : null;
          if (!steps && Array.isArray(input.steps)) {
            steps = (input.steps as Array<Record<string, unknown>>)
              .filter((s) => s && typeof s.say === 'string')
              .slice(0, 4)
              .map((s) => ({ anchor: typeof s.anchor === 'string' ? s.anchor : undefined, route: typeof s.route === 'string' ? s.route : undefined, say: String(s.say) }));
          }
          if (!steps?.length) return { output: 'No steps to show — say it in words instead.' };
          return { output: `Showing them ${steps.length} step(s) on screen, spoken as they go. Add nothing more.`, patch: { replay: steps } };
        }

        case 'go': {
          const route = str('route');
          if (!route) return { output: 'No address given.' };
          if (!isPathAllowed(app.experience, route)) {
            return { output: `That screen is not in ${app.experience} mode. Offer set_mode instead of sending them there.` };
          }
          app.closeOverlays();
          navigate(route);
          const screen = screenById(screenIdFor(route) ?? '');
          return { output: `Taken to ${route}.`, patch: { did: [t('assistant.did.went', { screen: screen ? screen.name[lang] : route })] } };
        }

        /*
         * "Where is my father?", "when is Miriam's birthday?" — shown, on the
         * screen they are already on.
         *
         * An answer about one person that leaves the screen as it was is half an
         * answer: the family tree should zoom to her, the globe should fly to
         * where she lives, the calendar should turn to her birthday. Each screen
         * already knows how to arrive on one thing — `/tree?focus=`,
         * `/world?focus=`, `/timeline?person=`, `/calendar?person=` — so this tool
         * is those links, chosen by where the person is standing rather than by
         * the model, which only has to say *who*. Anywhere without a view of its
         * own (a form, Settings, Admin controls) gets the person's card on top,
         * so nothing half-typed is navigated away from.
         *
         * Every id is checked against what the app actually holds. A model that
         * invented one would otherwise send somebody to an empty screen with
         * nothing selected, which looks exactly like the app being broken.
         */
        case 'show': {
          // The screen they are on *now*. This callback is not rebuilt when they
          // move between tabs, so the router's path from when it was made would
          // zoom the tree for somebody looking at the globe.
          const here = screenIdFor(window.location.pathname);
          const storyId = str('story');
          const day = str('day');
          const personId = str('person') ?? str('id');

          if (storyId) {
            const story = app.stories.find((s) => s.id === storyId);
            if (!story) return { output: 'No such story, or it is not visible to them. Use lookup stories first.' };
            app.openStory(story.id);
            const what = story.title || t('stories.title');
            return { output: `Opened the story "${what}".`, patch: { did: [t('assistant.did.opened', { what })] } };
          }

          if (day) {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return { output: 'A day is written YYYY-MM-DD.' };
            if (!isPathAllowed(app.experience, '/calendar')) return { output: 'The calendar is hidden in Simple mode, so nothing was shown. Say the date in words.' };
            app.closeOverlays();
            navigate(`/calendar?day=${day}`);
            const label = new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'long' }).format(new Date(`${day}T12:00:00`));
            return { output: `The calendar is open on ${day}.`, patch: { did: [t('assistant.did.showedDay', { day: label })] } };
          }

          if (!personId) return { output: 'Pass one of: a person id, a story id, or a day.' };
          const person = app.peopleById.get(personId as UUID);
          if (!person) return { output: 'No such person. Use lookup first, and pass back the id it gave you.' };
          const name = displayName(person, lang);
          const id = encodeURIComponent(person.id);
          const go = (to: string) => {
            app.closeOverlays();
            navigate(to);
          };
          const card = (why: string) => {
            app.openPersonCard(person.id);
            return { output: `${why}${name}'s card is open on top of the screen.`, patch: { did: [t('assistant.did.opened', { what: name })] } };
          };

          switch (here) {
            case 'tree':
              go(`/tree?focus=${id}`);
              return { output: `The tree is now zoomed to ${name}, with them selected.`, patch: { did: [t('assistant.did.showedOnTree', { name })] } };
            case 'world': {
              const where = person.status === 'deceased' ? null : whereIsAt(person, app.places, app.today);
              const loc = where ? app.locationsById.get(where.locationId) : undefined;
              if (loc?.lat === undefined || loc?.lng === undefined) return card(`${name} has no place on the globe, so `);
              go(`/world?focus=${id}`);
              return { output: `The globe has flown to where ${name} is.`, patch: { did: [t('assistant.did.showedOnGlobe', { name })] } };
            }
            case 'timeline':
              go(`/timeline?person=${id}`);
              return { output: `The timeline now shows only ${name}: their stories and their birth.`, patch: { did: [t('assistant.did.showedOnTimeline', { name })] } };
            case 'calendar':
              go(`/calendar?person=${id}`);
              return { output: `The calendar has turned to ${name}'s next occasion, with their card open.`, patch: { did: [t('assistant.did.showedOnCalendar', { name })] } };
            case 'profile':
              go(`/people/${id}`);
              return { output: `${name}'s profile is open.`, patch: { did: [t('assistant.did.opened', { what: name })] } };
            default:
              return card('');
          }
        }

        case 'open': {
          const what = str('what');
          const id = str('id');
          const say = (label: string) => ({ output: `Opened ${what}.`, patch: { did: [t('assistant.did.opened', { what: label })] } });
          switch (what) {
            case 'settings':
              app.openPanel('settings');
              return say(t('nav.settings'));
            case 'profile':
              app.openPanel('profile');
              return say(t('nav.me'));
            case 'notifications':
              if (!app.has('notifications')) return { output: 'The bell is hidden in Simple mode. Offer set_mode.' };
              app.openPanel('notifications');
              return say(t('nav.notifications'));
            case 'welcome':
              app.openWelcome('again');
              return say(t('greeting.menu'));
            case 'add':
              app.openAdd('menu');
              return say(t('add.tab'));
            case 'add.photo':
              app.openAdd('photo');
              return say(t('add.photo'));
            case 'add.event':
              app.openAdd('event');
              return say(t('add.event'));
            case 'add.milestone':
              app.openAdd('milestone');
              return say(t('add.milestone'));
            case 'add.recipe':
              // Opens the editor and stops there, like everything else it opens: the telling, and Publish, are a person's.
              app.openRecipeEditor();
              return say(t('add.recipe'));
            case 'record':
              navigate('/record');
              return say(t('nav.record'));
            case 'manage':
              if (!app.canBeAdmin && app.session?.role === 'member') return { output: 'They are not an editor or administrator, so Admin controls is not theirs to open.' };
              if (app.canBeAdmin && !app.adminMode) return { output: 'They are an administrator but Admin mode is off. Guide them to the switch in the menu (anchor menu.adminMode) instead.' };
              navigate('/manage');
              return say(t('nav.admin'));
            case 'person': {
              if (!id) return { output: 'Needs a person id from lookup.' };
              if (!app.peopleById.has(id as UUID)) return { output: 'No such person. Use lookup first.' };
              // On a tab with its own way of showing a person — the tree, the
              // globe, the timeline, the calendar — opening them *is* showing
              // them there; a card over a tree that did not move is half of it.
              if (SHOWING_SCREENS.has(screenIdFor(window.location.pathname) ?? '')) return runTool('show', { person: id });
              app.openPersonCard(id as UUID);
              return say(t('profile.title'));
            }
            case 'story': {
              if (!id) return { output: 'Needs a story id from lookup.' };
              if (!app.stories.some((s) => s.id === id)) return { output: 'No such story, or it is not visible to them.' };
              app.openStory(id as UUID);
              return say(t('stories.title'));
            }
            default:
              return { output: `Cannot open "${what}".` };
          }
        }

        case 'lookup': {
          const what = str('what') ?? 'people';
          return {
            output: runLookup(what, str('query'), {
              people: app.people,
              stories: app.stories,
              moments: app.moments,
              places: app.places,
              locationsById: app.locationsById,
              occasions: app.occasions,
              today: app.today,
              me: app.me,
              graph: app.graph,
              lang,
            }),
          };
        }

        case 'fill': {
          const form = str('form') as PrefillForm | undefined;
          const values = (input.values ?? {}) as Record<string, unknown>;
          if (!form) return { output: 'No form named.' };
          const clean = (k: string) => (typeof values[k] === 'string' ? (values[k] as string).slice(0, 300) : undefined);
          setPrefill(form, { title: clean('title'), notes: clean('notes'), caption: clean('caption'), detail: clean('detail') });
          if (form === 'add.event') app.openAdd('event');
          if (form === 'add.milestone') app.openAdd('milestone');
          if (form === 'add.photo') app.openAdd('photo');
          return { output: 'Filled in. They press Save themselves — you cannot.', patch: { did: [t('assistant.did.filled')] } };
        }

        case 'report': {
          const kind = (str('kind') ?? 'fault') as ReportKind;
          const title = str('title');
          if (!title) return { output: 'A report needs a one-line title.' };
          // Offered, never sent behind their back: the person presses Send.
          return { output: 'Offered to send it. They decide.', patch: { offer: { kind, title, detail: str('detail') } } };
        }

        case 'set_mode': {
          const mode = str('mode');
          if (mode !== 'simple' && mode !== 'advanced') return { output: 'Mode must be simple or advanced.' };
          app.setExperience(mode);
          return { output: `Switched to ${mode} mode.`, patch: { did: [t('assistant.did.mode', { mode: t(mode === 'advanced' ? 'settings.advanced' : 'assistant.mode.simple') })] } };
        }

        case 'set_language': {
          const next = str('lang');
          if (next !== 'en' && next !== 'he') return { output: 'Language must be en or he.' };
          setLang(next);
          void repos.auth.updatePreferredLang(next);
          return { output: 'Language switched.', patch: { did: [t('assistant.did.lang')] } };
        }

        // ---- the built-in provider's own vocabulary ----

        case 'explain': {
          const rule = RULES.find((r) => r.id === str('rule'));
          const limit = LIMITS.find((l) => l.id === str('limit'));
          const body = rule ? rule.says[lang] : limit ? limit.says[lang] : '';
          if (!body) return { output: '' };
          const suffix = limit && limit.kind === 'prototype' ? ` ${t('assistant.limit.prototype')}` : limit ? ` ${t('assistant.limit.design')}` : '';
          /*
           * No complaint form here, even when they said "broken".
           *
           * They asked why something behaves as it does and have just been told.
           * Putting a form in front of somebody who may well be satisfied reads
           * as "that probably didn't help, did it" — and the quiet "That didn't
           * help" line under every answer is already the way through for anyone
           * it really did fail. A form is only pushed forward when the assistant
           * has nothing to offer at all (see `offerReport`).
           */
          return { output: body, patch: { text: body + suffix } };
        }

        /*
         * "Who is around my age?", answered in a sentence.
         *
         * The `lookup` tool answers the same question in rows with ids on them,
         * which is right for a model and wrong for a person — nobody wants
         * "p_14 · Dana Edry · 1994 · age 32" read aloud to them. This is the
         * built-in provider's version: the same arithmetic, said the way one
         * person would say it to another, with the list cut at six because a
         * spoken sentence with eleven names in it is not a sentence.
         */
        case 'sameAge': {
          const near = bornNear({
            people: app.people,
            stories: app.stories,
            moments: app.moments,
            places: app.places,
            locationsById: app.locationsById,
            occasions: app.occasions,
            today: app.today,
            me: app.me,
            graph: app.graph,
            lang,
          });
          if (near.why === 'noMe') return { output: '', patch: { text: t('assistant.age.noMe') } };
          if (near.why === 'noYear') return { output: '', patch: { text: t('assistant.age.noYear') } };
          if (!near.people.length) return { output: '', patch: { text: t('assistant.age.none') } };
          const names = near.people.slice(0, 6).map((p) => displayName(p, lang) || displayName(p, lang === 'he' ? 'en' : 'he'));
          const rest = near.people.length - names.length;
          const who = names.join(lang === 'he' ? ', ' : ', ') + (rest > 0 ? t('assistant.age.more', { n: rest }) : '');
          return { output: `Told them about ${near.people.length} people born near ${near.year}.`, patch: { text: t('assistant.age.some', { who }) } };
        }

        case 'offerReport': {
          const kind = (str('kind') ?? 'fault') as ReportKind;
          return { output: '', patch: { text: t('assistant.offerReport'), offer: { kind, title: '', detail: str('detail') } } };
        }

        case 'suggest': {
          const ids = Array.isArray(input.howtos) ? (input.howtos as string[]).filter((x) => typeof x === 'string') : [];
          return {
            output: '',
            patch: { text: input.unsure === true ? t('assistant.unsure') : '', howtos: ids.length ? ids : undefined },
          };
        }

        default:
          return { output: `There is no tool called "${name}".` };
      }
    },
    [navigate, lang, t, setLang, repos, stepsFromHowTo],
  );

  // ---- asking -------------------------------------------------------------

  const ask = useCallback(
    (question: string) => {
      const text = question.trim();
      if (!text) return;
      lastQuestion.current = text;
      turnsRef.current = [{ role: 'user' as const, text }];
      setAnswer(null);
      setHearing(false);
      setStage('thinking');

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      void (async () => {
        const reply: Answer = { id: uuid(), text: '', did: [] };
        try {
          /*
           * A question asked before the app has heard back from its server — the
           * first press after a reload, or on a slow connection — waits for that
           * answer rather than going to the built-in helper. Otherwise the first
           * question of a visit got a canned answer in the robot voice and the
           * second got the AI, which looks like the assistant flickering between
           * two different things. When the server is known to be absent this
           * returns at once.
           */
          let asker = provider;
          if (!settingsRef.current.server) {
            const status = await detectServer();
            if (controller.signal.aborted) return;
            if (status) asker = createProvider(applyServerStatus(status));
          }

          const { experience, session } = appRef.current;
          const system = buildSystemPrompt(snapshot(), experience, session?.role === 'admin' || session?.role === 'editor');
          const tools = MODEL_TOOLS;

          logAssistant('model', 'ok', `asking ${asker.id}${asker.id === 'local' ? ' (the built-in matcher — no model is connected)' : ''}`, text);

          /** Everybody (and every story) the lookups mentioned, for showing who the answer is about. */
          const looked: string[] = [];
          let lookedUp = false;
          /** The model already put something on screen, or deliberately went somewhere. */
          let steered = false;

          for (let round = 0; round < MAX_ROUNDS; round++) {
            const res = await asker.ask({ system, turns: turnsRef.current, tools: [...tools], signal: controller.signal });
            logAssistant('model', 'ok', `answered with ${res.calls.length ? res.calls.map((c) => c.name).join(', ') : 'words only'}`, res.text || undefined);
            if (res.text) reply.text = reply.text ? `${reply.text}\n${res.text}` : res.text;
            if (!res.calls.length) break;

            turnsRef.current = [...turnsRef.current, { role: 'assistant' as const, text: res.text || undefined, calls: res.calls, providerData: res.providerData }];
            const results: ToolResult[] = [];
            const outcomes: ToolOutcome[] = [];
            for (const call of res.calls) {
              const { output, patch } = await runTool(call.name, call.input);
              logAssistant('tool', 'ok', call.name, call.input);
              if (patch) {
                if (patch.text) reply.text = patch.text;
                if (patch.did) reply.did.push(...patch.did);
                if (patch.replay) reply.replay = patch.replay;
                if (patch.howtos) reply.howtos = patch.howtos;
                if ('offer' in patch) reply.offer = patch.offer;
              }
              outcomes.push({ name: call.name, worked: !!patch });
              results.push({ id: call.id, output: output || 'done' });
              if (call.name === 'lookup') {
                lookedUp = true;
                looked.push(...idsIn(output));
              }
              if (patch && STEERING_TOOLS.has(call.name)) steered = true;
            }
            // Stop here when the answer is already on screen — see followUp.ts.
            if (!needsFollowUp(outcomes, !!reply.text, !!reply.replay?.length)) break;
            turnsRef.current = [...turnsRef.current, { role: 'user' as const, results }].slice(-KEEP_TURNS);
          }

          /*
           * The answer is about somebody, and the screen has not moved: show them.
           *
           * The model is asked to call `show` itself, and often answers without
           * doing it — "who is my father?" answered perfectly on a tree that stayed
           * exactly where it was. So the app does what the answer implies: whoever
           * the model looked up and then named first is shown on the screen they
           * are on (the tree zooms, the globe flies, the timeline narrows, the
           * calendar turns). On the timeline a story named in the answer comes
           * first, because stories are what that screen is for. Nothing happens
           * when the model already moved the screen itself, started a walkthrough,
           * or never looked at the family at all. See tools/autoShow.ts.
           */
          if (lookedUp && !steered && reply.text && !reply.replay && !reply.offer && !controller.signal.aborted) {
            const app = appRef.current;
            const seen = new Set(looked);
            let target: { person?: string; story?: string } | null = null;
            if (screenIdFor(window.location.pathname) === 'timeline') {
              const story = firstTitled(reply.text, app.stories.filter((s) => seen.has(s.id)));
              if (story) target = { story };
            }
            if (!target) {
              const candidates = looked.map((id) => app.peopleById.get(id as UUID)).filter((p): p is NonNullable<typeof p> => !!p);
              const living = app.people.filter((p) => !p.archived && !p.mergedIntoId);
              const person = firstNamed(reply.text, candidates, app.me?.id) ?? firstUniquelyNamed(reply.text, living, app.me?.id);
              if (person) target = { person };
            }
            if (target) {
              const { patch } = await runTool('show', target);
              logAssistant('tool', patch ? 'ok' : 'warn', 'show — chosen by the app from the answer, because the model did not', target);
              if (patch?.did) reply.did.push(...patch.did);
            }
          }
        } catch (e) {
          if (controller.signal.aborted) return;
          logAssistant(
            'model',
            'fail',
            `${provider.id === 'local' && settingsRef.current.server?.answer ? 'server' : provider.id} could not answer`,
            e instanceof ProviderError ? [e.status ? `HTTP ${e.status}` : '', e.detail].filter(Boolean).join(' · ') || e.message : e,
          );
          reply.text = t(e instanceof ProviderError ? e.messageKey : 'assistant.error.unknown');
          reply.error = true;
          // Anything the assistant itself could not do is still worth telling
          // the administrators about, so the offer rides on the failure.
          reply.offer = { kind: 'fault', title: '', detail: text };
        }

        if (!reply.text && !reply.replay && !reply.howtos && !reply.offer) reply.text = t('assistant.unsure');
        setAnswer(reply);

        /*
         * A walkthrough *is* the answer, so the screen goes straight to it rather
         * than making somebody read a summary and then press "show me". The
         * overlay closes and each step is spoken as it lights up (see Spotlight).
         */
        if (reply.replay?.length) {
          startWalk(reply.replay);
          return;
        }
        setStage('answer');
        if (reply.text && !reply.error && speaksAloudRef.current) void speakRef.current(reply.text);
      })();
    },
    [provider, runTool, snapshot, t, startWalk],
  );

  // ---- the microphone -----------------------------------------------------

  const clearSilence = () => {
    if (silence.current) clearTimeout(silence.current);
    silence.current = null;
  };

  const beginListening = useCallback(() => {
    const run = ++listenRun.current;
    setTranscript('');
    setStage('listening');
    /*
     * The silence watchdog. Restarted every time another word arrives, so it
     * only ever fires after a real pause — and then ends the recording exactly
     * as pressing stop would. A recording that only stops when told to is one
     * that people forget to stop.
     */
    const armSilence = () => {
      clearSilence();
      silence.current = setTimeout(() => listeningRef.current?.stop(), SILENCE_MS);
    };
    /** The microphone ripples while sound is arriving, and settles a moment after it stops. */
    const heard = () => {
      setHearing(true);
      if (hearingTimer.current) clearTimeout(hearingTimer.current);
      hearingTimer.current = setTimeout(() => setHearing(false), 450);
    };
    armSilence();
    void stt
      .start(
        lang,
        {
          onPartial: (partial) => {
            setTranscript(partial);
            if (partial.trim()) {
              armSilence();
              heard();
            }
          },
          // A recording sent to the server shows no words until the end; sound
          // on the microphone is what keeps it from being cut off mid-question.
          onSound: () => {
            armSilence();
            heard();
          },
          onFinal: (final) => {
            listeningRef.current = null;
            clearSilence();
            if (final.trim()) {
              // Straight to asking. What they said stays on screen, and the
              // button becomes the way to call it back.
              setTranscript(final.trim());
              askRef.current(final.trim());
            } else {
              /*
               * They opened it and said nothing.
               *
               * This used to drop back to a microphone waiting to be pressed. It
               * cannot any more: opening *is* the press, so a screen sitting
               * there with a microphone on it is a dead end — the only way to
               * ask would be to close it and press the corner again. So a
               * recording with nothing in it simply ends the whole thing, and
               * the button flies back to the corner where they can press it
               * again.
               */
              setTranscript('');
              setStage('closed');
            }
          },
          onError: (key) => {
            listeningRef.current = null;
            clearSilence();
            logAssistant('ear', 'fail', `listening with ${settingsRef.current.listenWith} failed`, key);
            setAnswer({ id: uuid(), text: t(key), did: [], error: true });
            setStage('answer');
          },
        },
        { continuous: true },
      )
      .then((handle) => {
        // Closed while the microphone was still starting: that recording is
        // thrown away too, rather than carrying on and sending itself later.
        if (run !== listenRun.current) handle.cancel();
        else listeningRef.current = handle;
      });
  }, [stt, lang, t]);

  /**
   * The one press: finish if it is listening, start if it is not.
   *
   * Finishing does not throw anything away — whatever has been heard so far is
   * sent, which is what somebody who has finished their sentence means by
   * pressing it. Throwing it away is the ✕, and it is deliberately a different
   * button in a different place.
   */
  const toggleMic = useCallback(() => {
    unlockAudio();
    stopSpeaking();
    if (stage === 'listening') {
      listeningRef.current?.stop();
      return;
    }
    if (!canListen) return;
    beginListening();
  }, [stage, canListen, beginListening, stopSpeaking]);

  askRef.current = ask;

  const askText = useCallback(
    (question: string) => {
      if (!question.trim()) return;
      unlockAudio();
      setTranscript(question.trim());
      ask(question);
    },
    [ask],
  );

  // ---- opening and closing ------------------------------------------------

  /**
   * Press the corner button and it is already listening.
   *
   * The old way opened a dark screen with a microphone on it and waited to be
   * pressed a second time, which is a step that exists only because the code was
   * built in that order. Nobody presses Help in order to look at a microphone.
   *
   * `stage` is set to `ready` first and `beginListening` moves it straight on to
   * `listening` — the in-between only exists for a browser that cannot hear,
   * where the overlay opens on the keyboard instead.
   */
  const openAssistant = useCallback(
    (from?: Origin) => {
      // Inside the tap, before anything waits: the only moment a phone lets a
      // page arrange to make sound later. See voice/player.ts.
      unlockAudio();
      // A new question silences the last answer rather than talking over it.
      stopSpeaking();
      setWalk(null);
      setAnswer(null);
      setTranscript('');
      setHearing(false);
      if (from) setOrigin(from);
      setStage('ready');
      if (canListen) beginListening();
      /*
       * While they are saying their question, have the model read the app map.
       * The question then arrives at a model that has already read it, and the
       * answer starts sooner. Costs nothing if the model read it in the last few
       * minutes, and the provider skips it then.
       */
      if (provider.warm) {
        const { experience, session } = appRef.current;
        provider.warm({ system: buildSystemPrompt(snapshot(), experience, session?.role === 'admin' || session?.role === 'editor'), tools: MODEL_TOOLS });
      }
      // A server that could not be reached when the app loaded gets another chance.
      if (!settingsRef.current.server) {
        void detectServer().then((status) => {
          if (status) applyServerStatus(status);
        });
      }
    },
    [canListen, beginListening, provider, snapshot, stopSpeaking],
  );

  /**
   * The ✕: stop everything, whatever stage it is at.
   *
   * A recording is thrown away unsent, a question on its way is called back, an
   * answer being read out goes quiet, and the screen closes. One press, always
   * the same meaning — which is what makes it safe to press when you are not
   * sure what you have started.
   */
  const close = useCallback(() => {
    abortRef.current?.abort();
    listenRun.current++;
    listeningRef.current?.cancel();
    listeningRef.current = null;
    clearSilence();
    tts.stop();
    setSpeaking(false);
    setHearing(false);
    setStage('closed');
    setTranscript('');
    setAnswer(null);
  }, [tts]);

  /**
   * What to offer before they have said anything.
   *
   * Chosen for where they are standing: somebody on the tree is probably looking
   * for a person, somebody in Simple mode has not found the other screens yet.
   * Two at most — these sit under a microphone, not instead of it, and a third
   * was one row more than the screen could hold without either clipping it or
   * pushing the button up out of thumb's reach.
   */
  const suggestions = useMemo(() => {
    const here = screenIdFor(path);
    const preferred: string[] = [];
    if (here === 'tree') preferred.push('findPerson', 'addRelative');
    if (here === 'timeline' || here === 'calendar') preferred.push('addEvent', 'recordStory');
    if (app.experience === 'simple') preferred.push('advancedMode');
    const ids = [...new Set([...preferred, ...HOWTOS.filter((h) => h.suggest).map((h) => h.id)])];
    return ids.slice(0, 2);
  }, [path, app.experience]);

  const value: AssistantValue = {
    stage,
    open: stage !== 'closed',
    openAssistant,
    close,
    origin,
    transcript,
    hearing,
    toggleMic,
    answer,
    askText,
    runHowTo,
    showSteps,
    walk,
    nextStep,
    endWalk,
    sendReport,
    suggestions,
    settings,
    canListen,
    canSpeak,
    speaksAloud,
    speaking,
    speak,
    stopSpeaking,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAssistant(): AssistantValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('AssistantProvider missing');
  return v;
}

/** The name of the button a step points at, for the caption. */
export function stepLabel(anchor: string | undefined, lang: 'en' | 'he'): string | null {
  if (!anchor) return null;
  return controlById(anchor)?.name[lang] ?? null;
}
